import React, { Fragment, useMemo } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl/dist';

// Components

import Form from '../../../lib/wrappers/Form';
import UniversalInput from '../../../lib/wrappers/UniversalInput';

// ActionCreators

import { changeData } from '../../../actions/ssap/ssapActionCreators';

// Utils

import { normalizeNameOfPerson } from '../../../utils/normalize/normalizeNameOfPerson';

// Constants

import { INPUT_TYPES } from '../../../constants/inputs/inputTypes';
import { INLINE_ELIGIBILITY_VALUES } from '../../../constants/ssap/miscellaneous/miscellaneous';
import { DROPDOWN_OPTIONS } from '../../../constants/ssap/hhmTypes/dropdownOptions';
import { VALIDATION_TYPES } from '../../../constants/inputs/validationTypes';



/**
 *
 * @param {Object} props
 * @returns {React.Component} - 'Additional Information - Member Enrolled In Other Health Insurance
 *
 */
const Page4_66 = props => {

    const {
        NOT_APPLICABLE,
        YES
    } = INLINE_ELIGIBILITY_VALUES;

    const {
        data,
        formName,
        familyStatus,
        household,
        householdMembers,
        hhmIndex : member,
        inlineCurrentIncomeEnabled,
        currentHhm
    } = props;

    const {
        applyingForCoverageIndicator,
        personId,
        healthCoverage: {
            absentParent,
            eligibleITU,
            receivedITU,
            unpaidBill
        },
        americanIndianAlaskaNative: {
            memberOfFederallyRecognizedTribeIndicator
        },
        name,
        inlineEligibility: {
            preliminaryMedicaidStatus,
            preliminaryEmergencyMedicaidStatus,
            preliminaryChipStatus,
        }
    } = currentHhm;

    const onFieldChange = (newData) => {
        const newState = { ...data };
        newState.taxHousehold.householdMember[member] = { ...newData };
        props.actions.changeData(newState);
    };

    const onFieldChange_needChildCoverage = (...args) => {
        const [ newData, indexInArray ] = args;

        const newState = { ...data };

        newState.taxHousehold.householdMember[indexInArray] = newData;

        newState.taxHousehold.householdMember[member].healthCoverage.coveredDependentChild =
            familyStatus.relatedPersonsIds.children.every(child => {
                const {
                    healthCoverage: {
                        childHaveCurrentCoverage
                    }
                } = householdMembers[child-1];

                return childHaveCurrentCoverage === true;
            });

        props.actions.changeData(newState);
    };

    const childrenHasHealthCoverage = (familyStatus) => {
        if(!familyStatus.relatedPersonsIds.children ||  household.primaryTaxFilerPersonId !== personId) return null;

        const filteredChildren = familyStatus.relatedPersonsIds.children.filter(ch => {
            const inlineEligibilityCriteria = [
                preliminaryMedicaidStatus
            ];

            if(inlineCurrentIncomeEnabled && inlineEligibilityCriteria.every(v => v !== NOT_APPLICABLE)) {
                return !householdMembers[ch - 1].applyingForCoverageIndicator &&
                    preliminaryMedicaidStatus === YES;
            } else {
                return !householdMembers[ch - 1].applyingForCoverageIndicator;
            }
        });

        return filteredChildren.length > 0
            ? (
                <div className = 'grid-row gi-component gi-checkbox' >
                    <fieldset className = 'usa-fieldset' >
                        <legend className = 'required-false text-normal' >
                            <FormattedMessage id = 'ssap.input.legend.children_has_health_coverage' />
                        </legend>
                        <ul className = 'usa-list usa-list--unstyled' >
                            {
                                filteredChildren.map((child) => {

                                    const currentChild = householdMembers[child-1];

                                    const {
                                        applicantGuid,
                                        name,
                                        healthCoverage: {
                                            childHaveCurrentCoverage
                                        }
                                    } = currentChild;

                                    return (
                                        <li key = { applicantGuid } >
                                            <UniversalInput
                                                label = { {
                                                    text: normalizeNameOfPerson(name)
                                                } }
                                                inputData = { {
                                                    isInputRequired: false,
                                                    type: INPUT_TYPES.CHECKBOX,
                                                    name: 'childHaveCurrentCoverage',
                                                    classes: [ '' ],
                                                    checked: childHaveCurrentCoverage,
                                                    value: childHaveCurrentCoverage,
                                                    currentData: currentChild,
                                                    fields: [ 'healthCoverage' ],
                                                    isInputStandalone: true
                                                } }
                                                inputActions = { {
                                                    onFieldChange: onFieldChange_needChildCoverage
                                                } }
                                                indexInArray = { child-1 }
                                                errorClasses = { [ 'margin-l-0' ] }
                                            />
                                        </li>
                                    );
                                })
                            }
                        </ul>
                    </fieldset>
                </div>
            )
            : null;
    };

    const isMemberTheChildWithoutParent = (()=> {
        const analyzeIfNotAllParents = value => !Array.isArray(value) ? true : value.length < 2;
        return applyingForCoverageIndicator && !familyStatus.currentHhm.isOlderThanChildAge && analyzeIfNotAllParents(familyStatus.relatedPersonsIds.parents);
    })();

    const isIndianHealthServicesShown = useMemo(() => {
        const inlineEligibilityCriteria = [
            preliminaryMedicaidStatus,
            preliminaryEmergencyMedicaidStatus,
            preliminaryChipStatus
        ];

        if (inlineCurrentIncomeEnabled && inlineEligibilityCriteria.every(v => v !== NOT_APPLICABLE)) {
            return applyingForCoverageIndicator &&
                memberOfFederallyRecognizedTribeIndicator &&
                inlineEligibilityCriteria.some(v => v === YES);
        } else {
            return applyingForCoverageIndicator && memberOfFederallyRecognizedTribeIndicator;
        }
    }, [
        applyingForCoverageIndicator,
        memberOfFederallyRecognizedTribeIndicator,
        inlineCurrentIncomeEnabled,
        preliminaryMedicaidStatus,
        preliminaryEmergencyMedicaidStatus,
        preliminaryChipStatus
    ]);

    const isRetroactiveAssistanceShown = useMemo(() => {
        const inlineEligibilityCriteria = [
            preliminaryMedicaidStatus,
            preliminaryEmergencyMedicaidStatus
        ];

        if (inlineCurrentIncomeEnabled && inlineEligibilityCriteria.every(v => v !== NOT_APPLICABLE)) {
            return applyingForCoverageIndicator && inlineEligibilityCriteria.some(v => v === YES);
        } else {
            return applyingForCoverageIndicator;
        }
    }, [
        applyingForCoverageIndicator,
        inlineCurrentIncomeEnabled,
        preliminaryMedicaidStatus,
        preliminaryEmergencyMedicaidStatus
    ]);

    const isAbsentParentShown = useMemo(() => {
        const inlineEligibilityCriteria = [
            preliminaryMedicaidStatus,
            preliminaryEmergencyMedicaidStatus
        ];

        if (inlineCurrentIncomeEnabled && inlineEligibilityCriteria.every(v => v !== NOT_APPLICABLE)) {
            return isMemberTheChildWithoutParent && inlineEligibilityCriteria.some(v => v === YES);
        } else {
            return isMemberTheChildWithoutParent;
        }
    }, [
        isMemberTheChildWithoutParent,
        inlineCurrentIncomeEnabled,
        preliminaryMedicaidStatus,
        preliminaryEmergencyMedicaidStatus
    ]);

    const replacements = {
        personName: (
            <strong>
                { normalizeNameOfPerson(name) }
            </strong>
        )
    };

    return (
        <Fragment>
            <Form
                { ...{
                    formName
                } }
            >
                <div
                    className = 'fade-in'
                    id = 'aiStateHealthBenefit'
                >
                    <div className = 'subsection' >
                        {
                            isIndianHealthServicesShown &&

                            <Fragment>
                                <UniversalInput
                                    legend = { {
                                        legendText: 'ssap.input.legend.eligible_for_tribal_program',
                                        legendTextReplacements: replacements
                                    } }
                                    label = { {
                                        showRequiredIcon: true
                                    } }
                                    inputData = { {
                                        isInputRequired: true,
                                        type: INPUT_TYPES.RADIO_BUTTONS,
                                        name: 'eligibleITU',
                                        value: eligibleITU,
                                        options: DROPDOWN_OPTIONS.YES_NO,
                                        validationType: VALIDATION_TYPES.CHOSEN,
                                        currentData: householdMembers[member],
                                        fields: [ 'healthCoverage' ],
                                        isInputStandalone: true
                                    } }
                                    inputActions = { {
                                        onFieldChange: onFieldChange
                                    } }
                                    errorClasses = { [ 'margin-l-0' ] }
                                />
                                <UniversalInput
                                    legend = { {
                                        legendText: 'ssap.input.legend.services_from_tribal_program',
                                        legendTextReplacements: replacements
                                    } }
                                    label = { {
                                        showRequiredIcon: true
                                    } }
                                    inputData = { {
                                        isInputRequired: true,
                                        type: INPUT_TYPES.RADIO_BUTTONS,
                                        name: 'receivedITU',
                                        value: receivedITU,
                                        options: DROPDOWN_OPTIONS.YES_NO,
                                        validationType: VALIDATION_TYPES.CHOSEN,
                                        currentData: householdMembers[member],
                                        fields: [ 'healthCoverage' ],
                                        isInputStandalone: true
                                    } }
                                    inputActions = { {
                                        onFieldChange: onFieldChange
                                    } }
                                    errorClasses = { [ 'margin-l-0' ] }
                                />
                            </Fragment>

                        }
                        {
                            isRetroactiveAssistanceShown  &&

                            <UniversalInput
                                legend = { {
                                    legendText: 'ssap.input.legend.non_internationalized_text',
                                    legendTextReplacements: {
                                        text: (
                                            <Fragment>
                                                <p className = 'required-true' >
                                                    <FormattedMessage
                                                        id = 'ssap.input.legend.medical_bills'
                                                        values = { replacements }
                                                    />
                                                </p>
                                                <FormattedMessage id = 'ssap.input.legend.medical_bills.note' />
                                            </Fragment>
                                        )
                                    }
                                } }
                                label = { {
                                    showRequiredIcon: false
                                } }
                                inputData = { {
                                    isInputRequired: true,
                                    type: INPUT_TYPES.RADIO_BUTTONS,
                                    name: 'unpaidBill',
                                    value: unpaidBill,
                                    options: DROPDOWN_OPTIONS.YES_NO,
                                    validationType: VALIDATION_TYPES.CHOSEN,
                                    currentData: householdMembers[member],
                                    fields: [ 'healthCoverage' ],
                                    isInputStandalone: true
                                } }
                                inputActions = { {
                                    onFieldChange: onFieldChange
                                } }
                                errorClasses = { [ 'margin-l-0' ] }
                            />
                        }
                        {
                            childrenHasHealthCoverage(familyStatus)
                        }
                        {
                            isAbsentParentShown &&

                            <UniversalInput
                                legend = { {
                                    legendText: 'ssap.input.legend.parents_live_outside',
                                    legendTextReplacements: replacements
                                } }
                                label = { {
                                    showRequiredIcon: true
                                } }
                                inputData = { {
                                    isInputRequired: true,
                                    type: INPUT_TYPES.RADIO_BUTTONS,
                                    name: 'absentParent',
                                    value: absentParent,
                                    options: DROPDOWN_OPTIONS.YES_NO,
                                    validationType: VALIDATION_TYPES.CHOSEN,
                                    currentData: householdMembers[member],
                                    fields: [ 'healthCoverage' ],
                                    isInputStandalone: true
                                } }
                                inputActions = { {
                                    onFieldChange: onFieldChange
                                } }
                                errorClasses = { [ 'margin-l-0' ] }
                            />
                        }
                    </div>
                </div>
            </Form>
        </Fragment>
    );
};

const mapStateToProps = state => ({
    data: state.ssap.data,

    household: state
        .ssap
        .data
        .taxHousehold,

    householdMembers: state
        .ssap
        .data
        .taxHousehold
        .householdMember
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        changeData
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page4_66);
